import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Plus, TrendingUp, Clock, Flame } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import { hapticFeedback } from '../utils/mobile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXERCISE_TYPES = [
  { value: 'cardio', label: 'Cardio', icon: '🏃' },
  { value: 'strength', label: 'Strength', icon: '💪' },
  { value: 'sports', label: 'Sports', icon: '⚽' },
  { value: 'other', label: 'Other', icon: '🏋️' }
];

const COMMON_EXERCISES = {
  cardio: [
    { name: 'Running', calsPerMin: 10 },
    { name: 'Cycling', calsPerMin: 7 },
    { name: 'Swimming', calsPerMin: 9 },
    { name: 'Walking', calsPerMin: 4 },
    { name: 'Elliptical', calsPerMin: 8 }
  ],
  strength: [
    { name: 'Weight Training', calsPerMin: 6 },
    { name: 'Bodyweight Exercises', calsPerMin: 5 },
    { name: 'CrossFit', calsPerMin: 9 },
    { name: 'Yoga', calsPerMin: 3 },
    { name: 'Pilates', calsPerMin: 4 }
  ],
  sports: [
    { name: 'Basketball', calsPerMin: 8 },
    { name: 'Soccer', calsPerMin: 9 },
    { name: 'Tennis', calsPerMin: 7 },
    { name: 'Volleyball', calsPerMin: 6 },
    { name: 'Golf', calsPerMin: 4 }
  ]
};

const ExerciseTracker = ({ token }) => {
  const { t } = useTranslation();
  const [exercises, setExercises] = useState([]);
  const [todayStats, setTodayStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newExercise, setNewExercise] = useState({
    name: '',
    type: 'cardio',
    duration_minutes: '',
    intensity: 'moderate',
    notes: ''
  });

  const fetchTodayExercises = async () => {
    try {
      const response = await axios.get(`${API}/exercise/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayStats(response.data);
      setExercises(response.data.exercises);
    } catch (error) {
      console.error('Error fetching exercises:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/exercise/history?days=7`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  useEffect(() => {
    fetchTodayExercises();
    fetchHistory();
  }, [token]);

  const calculateCalories = () => {
    const duration = parseInt(newExercise.duration_minutes) || 0;
    const exerciseList = COMMON_EXERCISES[newExercise.type] || [];
    const exercise = exerciseList.find(e => e.name === newExercise.name);
    
    if (exercise) {
      let calsPerMin = exercise.calsPerMin;
      if (newExercise.intensity === 'light') calsPerMin *= 0.7;
      if (newExercise.intensity === 'vigorous') calsPerMin *= 1.3;
      return Math.round(calsPerMin * duration);
    }
    
    // Default calculation
    let baseCals = 5;
    if (newExercise.intensity === 'light') baseCals = 3;
    if (newExercise.intensity === 'vigorous') baseCals = 8;
    return Math.round(baseCals * duration);
  };

  const logExercise = async () => {
    console.log('Attempting to log exercise:', newExercise);
    
    // Better validation
    if (!newExercise.type) {
      alert('Please select an exercise type first');
      return;
    }
    
    if (!newExercise.name || newExercise.name === 'custom') {
      alert('Please select or enter an exercise name');
      return;
    }
    
    if (!newExercise.duration_minutes || parseInt(newExercise.duration_minutes) <= 0) {
      alert('Please enter a valid duration in minutes');
      return;
    }

    const calories = calculateCalories();

    try {
      const exerciseData = {
        type: newExercise.type,
        name: newExercise.name,
        duration_minutes: parseInt(newExercise.duration_minutes),
        intensity: newExercise.intensity,
        calories_burned: calories
      };
      
      console.log('Sending exercise data:', exerciseData);
      
      const response = await axios.post(
        `${API}/exercise/log`,
        exerciseData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Exercise logged successfully:', response.data);

      // Reset form
      setNewExercise({
        name: '',
        type: 'cardio',
        duration_minutes: '',
        intensity: 'moderate',
        notes: ''
      });
      setShowAddForm(false);
      
      // Refresh data
      fetchTodayExercises();
      fetchHistory();
      
      alert(`Great! You burned ${calories} calories! 🔥`);
    } catch (error) {
      console.error('Error logging exercise:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to log exercise';
      alert(`Error: ${errorMessage}`);
    }
  };

  const chartData = history?.exercises?.slice(0, 7).reverse().map(ex => ({
    name: new Date(ex.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    calories: ex.calories_burned
  })) || [];

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden pb-8" data-testid="exercise-tracker">
      {/* Add Exercise Button - MOVED TO TOP */}
      <button
        onClick={() => setShowAddForm(!showAddForm)}
        className="w-full py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00] flex items-center justify-center gap-2"
        data-testid="log-exercise-btn"
      >
        <Plus size={20} />
        {t('exerciseTrack.logExercise')}
      </button>

      {/* Add Exercise Form - MOVED TO TOP */}
      {showAddForm && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-2 border-[#FF6600]">
          <h3 className="font-bold mb-4 dark:text-white">{t('exerciseTrack.logWorkout')}</h3>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2 dark:text-gray-200">{t('exerciseTrack.exerciseType')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {EXERCISE_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => {
                      console.log('Clicking exercise type:', type.value);
                      setNewExercise({ ...newExercise, type: type.value, name: '' });
                    }}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                      newExercise.type === type.value
                        ? 'border-[#FF6600] bg-orange-100 dark:bg-orange-900/30 text-[#FF6600]'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 text-gray-700 dark:text-gray-300 dark:text-gray-200'
                    }`}
                  >
                    <div className="text-xl mb-1">{type.icon}</div>
                    <div className="text-xs font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2 dark:text-gray-200">Exercise</label>
              <select
                value={newExercise.name}
                onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                className="w-full p-3 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg focus:outline-none focus:border-[#FF6600] bg-white dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('exerciseTrack.selectExercise')}...</option>
                {(COMMON_EXERCISES[newExercise.type] || []).map(ex => (
                  <option key={ex.name} value={ex.name}>{ex.name}</option>
                ))}
                <option value="custom">{t('exerciseTrack.customExercise')}</option>
              </select>
            </div>

            {newExercise.name === 'custom' && (
              <div>
                <label className="text-sm font-medium block mb-2">Custom Exercise Name</label>
                <input
                  type="text"
                  placeholder="Enter exercise name"
                  onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-2">{t('exerciseTrack.duration')}</label>
                <input
                  type="number"
                  value={newExercise.duration_minutes}
                  onChange={(e) => setNewExercise({ ...newExercise, duration_minutes: e.target.value })}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                  placeholder="30"
                />
              </div>

              <div>
                <label className="text-sm font-medium block mb-2">{t('exerciseTrack.intensity')}</label>
                <select
                  value={newExercise.intensity}
                  onChange={(e) => setNewExercise({ ...newExercise, intensity: e.target.value })}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                >
                  <option value="light">{t('exerciseTrack.light')}</option>
                  <option value="moderate">{t('exerciseTrack.moderate')}</option>
                  <option value="vigorous">{t('exerciseTrack.vigorous')}</option>
                </select>
              </div>
            </div>

            {newExercise.duration_minutes && (
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-[#FF6600]">
                <div className="font-bold text-[#FF6600]">
                  ≈ {calculateCalories()} calories burned
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium block mb-2">Notes (optional)</label>
              <textarea
                value={newExercise.notes}
                onChange={(e) => setNewExercise({ ...newExercise, notes: e.target.value })}
                className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                rows={3}
                placeholder="How did it feel?"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={logExercise} className="flex-1 py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00]">
                {t('exerciseTrack.logExercise')}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-900"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Today's Stats - MOVED TO BOTTOM */}
      {todayStats && (
        <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] p-4 rounded-xl shadow-sm text-white">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <Activity size={20} />
            {t('exerciseTrack.todaysSummary')}
          </h2>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs opacity-80 mb-1">Calories Burned</div>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Flame size={18} />
                {Math.round(todayStats.total_calories_burned)}
              </div>
            </div>
            <div>
              <div className="text-xs opacity-80 mb-1">Duration</div>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Clock size={18} />
                {todayStats.total_duration_minutes} min
              </div>
            </div>
            <div>
              <div className="text-xs opacity-80 mb-1">Adjusted Target</div>
              <div className="text-2xl font-bold flex items-center gap-2">
                <TrendingUp size={18} />
                {Math.round(todayStats.adjusted_calorie_target)}
              </div>
            </div>
          </div>

          {todayStats.total_calories_burned > 0 && (
            <div className="mt-4 bg-white dark:bg-gray-800/20 p-3 rounded-lg text-sm">
              💡 Great job! You've earned {Math.round(todayStats.total_calories_burned)} extra calories today!
            </div>
          )}
        </div>
      )}

      {/* Today's Exercises */}
      {exercises.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-3">{t('exerciseTrack.todaysExercises')}</h3>
          <div className="space-y-3">
            {exercises.map((exercise, idx) => (
              <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{exercise.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {exercise.duration_minutes} min • {exercise.intensity}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-[#FF6600]">{Math.round(exercise.calories_burned)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">calories</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 7-Day Chart */}
      {history && history.exercises.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">{t('exerciseTrack.weeklyChart')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="calories" fill="#FF6600" />
            </BarChart>
          </ResponsiveContainer>
          
          <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
            <div className="text-center">
              <div className="font-bold text-[#FF6600]">{Math.round(history.total_calories_burned)}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Total Calories</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-[#FF6600]">{history.total_workouts}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Workouts</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-[#FF6600]">{Math.round(history.total_duration_minutes / 60)}h</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Total Time</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseTracker;
